import { useState } from "react";
import axios from "axios";
import { Phone, MapPin, Send, CheckCircle2, Loader2 } from "lucide-react";
import { SITE } from "@/lib/site";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    nume: "",
    telefon: "",
    email: "",
    data_eveniment: "",
    nr_persoane: "",
    mesaj: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nume.trim() || !form.telefon.trim() || !form.mesaj.trim()) {
      toast.error("Te rugăm să completezi numele, telefonul și mesajul.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Mesajul a fost trimis! Te contactăm în cel mai scurt timp.");
      setForm({ nume: "", telefon: "", email: "", data_eveniment: "", nr_persoane: "", mesaj: "" });
    } catch (err) {
      console.error(err);
      toast.error("Nu am putut trimite mesajul. Te rugăm să încerci din nou sau să ne suni.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 lg:py-36 px-5 lg:px-10 bg-[#012a2d] text-[#fcf3e6] overflow-hidden">
      {/* decorative */}
      <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-[#048a93]/15 blur-3xl" />
      <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-[#d4b895]/10 blur-3xl" />

      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-20 relative">
        <div className="lg:col-span-5">
          <div className="text-[#048a93] uppercase text-[11px] tracking-[0.32em] font-semibold mb-5">
            <span className="inline-block h-px w-10 bg-[#048a93] align-middle mr-3" />
            Contact & Rezervări
          </div>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05]" data-testid="contact-title">
            Pregătiți<br />să creăm <span className="italic text-[#048a93]">amintiri</span>?
          </h2>
          <p className="mt-6 font-sans-ui text-[#fcf3e6]/75 leading-relaxed max-w-md">
            Lasă-ne câteva detalii despre evenimentul tău și te contactăm cât mai repede pentru a confirma disponibilitatea și a-ți pregăti o ofertă.
          </p>

          <div className="mt-12 space-y-7">
            <a href={SITE.phoneHref} className="flex items-start gap-4 group" data-testid="contact-info-phone">
              <div className="h-12 w-12 rounded-2xl bg-[#048a93]/15 group-hover:bg-[#048a93] flex items-center justify-center transition">
                <Phone className="h-5 w-5 text-[#048a93] group-hover:text-[#fcf3e6] transition" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#048a93] font-semibold">Telefon</div>
                <div className="font-serif-display text-2xl">{SITE.phoneDisplay}</div>
              </div>
            </a>
            <div className="flex items-start gap-4" data-testid="contact-info-location">
              <div className="h-12 w-12 rounded-2xl bg-[#048a93]/15 flex items-center justify-center">
                <MapPin className="h-5 w-5 text-[#048a93]" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#048a93] font-semibold">Locație</div>
                <div className="font-serif-display text-2xl">{SITE.location}</div>
                <div className="text-xs text-[#fcf3e6]/55 font-sans-ui mt-1">Adresa exactă se comunică la rezervare</div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {sent ? (
            <div className="bg-[#fcf3e6] text-[#012a2d] rounded-[2rem] p-10 lg:p-14 text-center" data-testid="contact-success">
              <div className="h-16 w-16 rounded-full bg-[#048a93] text-[#fcf3e6] flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="h-8 w-8" />
              </div>
              <h3 className="font-serif-display text-3xl lg:text-4xl">Mulțumim!</h3>
              <p className="font-sans-ui text-[#1a2421]/70 mt-3 max-w-md mx-auto">
                Mesajul tău a ajuns la noi. Te contactăm telefonic în cel mai scurt timp pentru a confirma disponibilitatea.
              </p>
              <button
                onClick={() => setSent(false)}
                className="mt-8 inline-flex items-center gap-2 border border-[#012a2d]/20 hover:border-[#048a93] hover:text-[#048a93] text-[#012a2d] rounded-full px-6 py-3 text-sm font-sans-ui transition"
                data-testid="contact-success-back"
              >
                Trimite un alt mesaj
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-[#fcf3e6] text-[#012a2d] rounded-[2rem] p-7 lg:p-10 space-y-5 shadow-2xl"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Nume" required>
                  <input
                    data-testid="contact-name-input"
                    type="text"
                    value={form.nume}
                    onChange={update("nume")}
                    placeholder="Numele tău"
                    className="w-full bg-transparent border-b border-[#012a2d]/20 py-3 focus:outline-none focus:border-[#048a93] font-sans-ui placeholder:text-[#012a2d]/30"
                  />
                </Field>
                <Field label="Telefon" required>
                  <input
                    data-testid="contact-phone-input"
                    type="tel"
                    value={form.telefon}
                    onChange={update("telefon")}
                    placeholder="07xx xxx xxx"
                    className="w-full bg-transparent border-b border-[#012a2d]/20 py-3 focus:outline-none focus:border-[#048a93] font-sans-ui placeholder:text-[#012a2d]/30"
                  />
                </Field>
              </div>

              <Field label="Email (opțional)">
                <input
                  data-testid="contact-email-input"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="exemplu@email.com"
                  className="w-full bg-transparent border-b border-[#012a2d]/20 py-3 focus:outline-none focus:border-[#048a93] font-sans-ui placeholder:text-[#012a2d]/30"
                />
              </Field>

              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Data evenimentului">
                  <input
                    data-testid="contact-date-input"
                    type="date"
                    value={form.data_eveniment}
                    onChange={update("data_eveniment")}
                    className="w-full bg-transparent border-b border-[#012a2d]/20 py-3 focus:outline-none focus:border-[#048a93] font-sans-ui text-[#012a2d]/80"
                  />
                </Field>
                <Field label="Număr persoane">
                  <input
                    data-testid="contact-guests-input"
                    type="text"
                    value={form.nr_persoane}
                    onChange={update("nr_persoane")}
                    placeholder="ex. 25 adulți + 8 copii"
                    className="w-full bg-transparent border-b border-[#012a2d]/20 py-3 focus:outline-none focus:border-[#048a93] font-sans-ui placeholder:text-[#012a2d]/30"
                  />
                </Field>
              </div>

              <Field label="Mesaj" required>
                <textarea
                  data-testid="contact-message-input"
                  value={form.mesaj}
                  onChange={update("mesaj")}
                  rows={4}
                  placeholder="Spune-ne mai multe despre eveniment (interval orar dorit, ocazia, întrebări)..."
                  className="w-full bg-transparent border-b border-[#012a2d]/20 py-3 focus:outline-none focus:border-[#048a93] font-sans-ui placeholder:text-[#012a2d]/30 resize-none"
                />
              </Field>

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#012a2d] hover:bg-[#048a93] text-[#fcf3e6] rounded-full pl-7 pr-3 py-3 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span className="font-sans-ui text-sm font-medium tracking-wide">
                  {loading ? "Se trimite..." : "Trimite mesajul"}
                </span>
                <span className="h-9 w-9 rounded-full bg-[#048a93] group-hover:bg-[#fcf3e6] text-[#fcf3e6] group-hover:text-[#048a93] flex items-center justify-center transition">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </span>
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.25em] text-[#048a93] font-semibold font-sans-ui">
      {label} {required && <span className="text-[#048a93]">*</span>}
    </span>
    {children}
  </label>
);
